import {FC, useEffect, useState, useRef} from "react";
import {PopupContainer, Container, BottomContainer, HeaderBox, StatBox} from "./CancelJobPage.styles";
import ModalTitleContainer from "components/Modal/components/ModalTitleContainer";
import styles from "./components.module.scss";
import useActionApi from "hooks/useActionApi";
import Api from "api";
import dayjs from "dayjs";
import {HygienistProps, HygienistScoreProps} from "utils/types";
import {JobProps} from "utils/types";
import {FormMaterialSelector} from "components/Selector";
import {FormMaterialInput} from "components/Inputs/MaterialInput";
import {useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import joi from "joi";
import FixedLoader from "components/FixedLoader/FixedLoader";
import DefaultButton from "components/Buttons/DefaultButton";
import {useHistory} from "react-router";
import duration from 'dayjs/plugin/duration'

import {
    Box,
    Typography,
    Button,
    Alert,
    AlertTitle,
    MenuItem,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    styled,
} from '@mui/material';
import {
    Warning as WarningIcon,
    ArrowBack as ArrowBackIcon,
    Help as HelpIcon,
    ExpandMore as ExpandMoreIcon,
    North as IncreaseIcon,
    South as DecreaseIcon,
    Remove as UnchangedIcon,
} from '@mui/icons-material';
import TopBarRedesigned from "../../../components/TopBarRedesigned/TopBarRedesigned";
import {exitReact, getReliabilityStatus} from "../../../utils";
import {StyledButton} from "../../../components/Buttons/DefaultButton/DefaultButton.styles";
import Popup from "../../../components/Popup";

dayjs.extend(duration);

const cancelThanksPopupDescription = 'We hope everything works out okay! Our support team has received the information about your cancellation and will be in contact about your account soon, hold tight.';

export const schema = joi
    .object({
        reason: joi.string().label('Reason').required(),
        details: joi.string().label('Details').required(),
    })
    .required()

const CancelThanksPopup = ({onClose}: {onClose: () => void}) => {

    return (
        <PopupContainer>
            <ModalTitleContainer title={'Got it, thanks'}/>

            <div className={styles.modalDescription}>
                {cancelThanksPopupDescription}
            </div>

            <DefaultButton
                variant="contained"
                size="large"
                className={styles.buttonColour}
                onClick={onClose}
            >
                Submit
            </DefaultButton>
        </PopupContainer>
    )
}

export const cancelReasons = [
    {
        value: 'illness',
        text: 'Illness',
    },
    {
        value: 'caregiver',
        text: 'Caregiver Related',
    },
    {
        value: 'transportation',
        text: 'Transportation Related',
    },
    {
        value: 'conflict',
        text: 'Scheduling Conflict',
    },
    {
        value: 'other',
        text: 'Other',
    }
]

interface StatsProps {
    label: string
    newValue: number | string
    currentValue: number | string
    color: string
}

const StatItem = ({ label, newValue, currentValue, color }: StatsProps) => {
    let icon = <UnchangedIcon sx={{color: 'text.secondary'}} />
    if (newValue > currentValue) {
        icon = <IncreaseIcon sx={{color: 'text.secondary'}} />
    } else if (newValue < currentValue) {
        icon = <DecreaseIcon sx={{color: 'text.secondary'}} />
    }

    return (<StatBox mb={1}>
        <div className="stat-label">
            {icon}
            <Typography component="span" fontWeight="bold">{`${label}: `}</Typography>
            <Typography component="span" color={color} fontWeight="bold">{newValue}</Typography>
        </div>
        {newValue !== currentValue && <Typography className="stat-current-value" component="span" color="text.secondary"> (Currently: {currentValue})</Typography>}
    </StatBox>);
}

const getConsequences = (cancellationStats: HygienistScoreProps, gracePeriod: boolean, less24h: boolean) => {

    let reliabilityString = <>from {cancellationStats.weightedReliability !== -1
         ? (cancellationStats.weightedReliability * 100).toFixed(0) : 'Not yet rated'} to {cancellationStats.newWeightedScore !== -1
         ? (cancellationStats.newWeightedScore * 100).toFixed(0) : 'Not yet rated'}</>;
    let reliabilityStatusString = <>from {cancellationStats.weightedReliability !== -1
         ? getReliabilityStatus(cancellationStats.weightedReliability) : 'Not yet rated'} to {cancellationStats.newWeightedScore !== -1
         ? getReliabilityStatus(cancellationStats.newWeightedScore) : 'Not yet rated'}</>;
    let cancellationScoreString = <>from {cancellationStats?.cancellationScore !== -1
         ? cancellationStats?.cancellationScore : 'Not yet rated'} to {cancellationStats?.newCancellationScore !== -1
         ? cancellationStats?.newCancellationScore : 'Not yet rated'}</>;

    if (cancellationStats.weightedReliability > cancellationStats.newWeightedScore) {
        reliabilityString = <>Your Reliability Score will decrease {reliabilityString}</>;
    } else if (cancellationStats.weightedReliability < cancellationStats.newWeightedScore) {
        reliabilityString = <>Your Reliability Score will increase {reliabilityString}</>;
    } else {
        reliabilityString = <>Your Reliability Score {cancellationStats.weightedReliability !== -1
            ? 'will remain unchanged ' + (cancellationStats.weightedReliability * 100).toFixed(0) : 'is not yet rated'}</>;
    }   

    if (cancellationStats.weightedReliability > cancellationStats.newWeightedScore) {
        reliabilityStatusString = <>Your Reliability Status will decrease {reliabilityStatusString}</>;
    } else if (cancellationStats.weightedReliability < cancellationStats.newWeightedScore) {
        reliabilityStatusString = <>Your Reliability Status will increase {reliabilityStatusString}</>;
    } else if(cancellationStats.weightedReliability < 0.6 && cancellationStats.newWeightedScore < 0.6) {
        reliabilityStatusString = <>Your Reliability Status will remain unchanged {getReliabilityStatus(cancellationStats.newWeightedScore)}</>;
    }

    if(cancellationStats.cancellationScore > cancellationStats.newCancellationScore) {
        cancellationScoreString = <>Your cancellation score will decrease {cancellationScoreString}</>;
    } else if (cancellationStats.cancellationScore < cancellationStats.newCancellationScore) {
        cancellationScoreString = <>Your cancellation score will increase {cancellationScoreString}</>;
    } else {
        cancellationScoreString = <>Your cancellation score {cancellationStats?.cancellationScore !== -1 ? 'will remain unchanged ' + cancellationStats?.cancellationScore : 'is not yet rated'}</>;
    }

    if (gracePeriod) {
        return [
            "This cancellation falls within the Grace Period",
            reliabilityString,
            // reliabilityStatusString,
            cancellationScoreString,
            "There will be no booking restrictions applied",
            "This cancellation will not count towards your total cancellations"
        ];
    }

    if (less24h) {
        return [
            reliabilityString,
            // reliabilityStatusString,
            cancellationScoreString,
            "You will be unable to book shift dates within the next 14 days",
            "This cancellation will count as a short-notice cancellation",
            "Remember, short-notice cancellations may result in account suspension"
        ];
    }

    return [
        reliabilityString,
        // reliabilityStatusString,
        cancellationScoreString,
        "You will be unable to book shift dates within the next 14 days",
        'Remember, repeated cancellations may result in account suspension'
    ];
}

interface CancelJobPageProps {
    job: JobProps,
    hygienist: HygienistProps,
    cancellationStatsProps?: HygienistScoreProps
}

const CancelJobPage: FC= () => {
    const history = useHistory()

    const {
        job,
        hygienist,
        cancellationStatsProps,
    } = history.location.state as CancelJobPageProps

    const [loading, setLoading] = useState<boolean>(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [showAllStats, setShowAllStats] = useState(false);
    const [cancelThanksPopup, setCancelThanksPopup] = useState<boolean>(false);
    const [openTooltip, setOpenTooltip] = useState(false);
    const [cancellationStats, setCancellationStats] = useState<HygienistScoreProps>({
        cancellationScore: 0,
        cancellations: 0,
        cancellations30Days: 0,
        cancellations90Days: 0,
        completed: 0,
        completed30Days: 0,
        completed90Days: 0,
        newCancellationScore: 0,
        newOverallScore: 0,
        newRecentScore: 0,
        newWeightedScore: 0,
        overallReliability: 0,
        recentReliability: 0,
        weightedReliability: 0
    });

    const {
        control,
        handleSubmit,
        watch,
    } = useForm({
        defaultValues: {
            reason: '',
            details: '',
        } as { reason: string, details: string },
        resolver: joiResolver(schema) as any,
    });

    const [statsLoading, , getStats] = useActionApi({
        apiMethod: Api.calculateCancellationStats,
        onSuccess: (data: HygienistScoreProps) => {
            setCancellationStats(data)
        },
    })

    useEffect(() => {
        if (!cancellationStatsProps) {
            getStats({jobId: job.id})
            return;
        }
        setCancellationStats(cancellationStatsProps)
        return;
    }, [cancellationStatsProps]);

    const onCancel = async (data: any) => {
        setLoading(true)
        try {
            const {hygienistCancelledId} = await Api.hygienistCancelJob(job.id)
            await Api.updateCancelled({...data, hygienistCancelledId})
            setLoading(false)
            setCancelThanksPopup(true)
        } catch (e) {
            setLoading(false)
        }
    }

    const calculateCancellationScore = (score: number) => {
        if(score === -1) return 'Not yet rated'
        return (score * 100).toFixed(0) + '%'
    }

    const start = dayjs(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedStart);
    const gracePeriod = cancellationStats.newCancellationScore === cancellationStats.cancellationScore;
    const less24h = start.diff(dayjs(), 'hour') < 24;

    const stats = {
        reliabilityScore: {
             new: calculateCancellationScore(cancellationStats.newWeightedScore),
             current: calculateCancellationScore(cancellationStats.weightedReliability)
             },
        reliabilityStatus: { new: getReliabilityStatus(cancellationStats.newWeightedScore), current: getReliabilityStatus(cancellationStats.weightedReliability) },
        cancellationScore: { new: cancellationStats?.newCancellationScore, current: cancellationStats?.cancellationScore },
        cancellationsLast90Days: { new: cancellationStats?.cancellations90Days + (gracePeriod ? 0 : 1), current: cancellationStats?.cancellations90Days },
        cancellationsLifetime: { new: cancellationStats?.cancellations + (gracePeriod ? 0 : 1), current: cancellationStats?.cancellations },
    };

    let hours = start.diff(dayjs(), 'hours')
    const days = Math.floor(hours / 24);
    hours = hours - (days * 24);

    let timeBeforeStartString = days
        ? `${days} days and ${hours} hours before the shift is due to start`
        : `${hours} hours before the shift is due to start`;

    if (days < 0) {
        timeBeforeStartString = 'after the shift has already started'
    }

    const consequences = getConsequences(cancellationStats, gracePeriod, less24h);

    let title = `${hygienist.firstName}, are you sure you want to cancel this shift?`;
    if (less24h) {
        title = 'Last minute cancellation?';
    }

    const goBack = async () => {
        history.goBack()
    }

    const goHome = () => {
        history.push('loading')
        exitReact('home', 'rld')
    }

    const bottomRef = useRef<HTMLDivElement>(null);

    const handleCancelClick = () => {
        setIsConfirming(true);
        setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    
    const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip {...props} classes={{ popper: className }} />
      ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
          backgroundColor: '#f5f5f9',
          color: 'rgba(0, 0, 0, 0.87)',
          maxWidth: 220,
          fontSize: theme.typography.pxToRem(12),
          border: '1px solid #dadde9',
        },
      }));

      const onOpenTooltip = () => {
        setOpenTooltip(!openTooltip)
      }

    return (
        <>
            <TopBarRedesigned onBack={goBack} title={title} />

            <Popup opened={cancelThanksPopup}>
                <CancelThanksPopup onClose={() => {
                    setCancelThanksPopup(false)
                    goHome()
                }} />
            </Popup>

            <Container>
                <HeaderBox mb={2} display="flex" gap={2}>
                    <div>
                        <Typography variant="h6" fontWeight="bold" color="text.primary" mb={1}>Shift Details</Typography>
                        <Typography color="text.secondary" fontWeight="bold">Date: {dayjs(job.shifts[0].shiftDate).format('dddd, MMM D')}</Typography>{/*July 15, 2024*/}
                        <Typography color="text.secondary" fontWeight="bold">Start at: {job.shifts[0].postedStart} - Shift ends: {job.shifts[0].postedEnd}</Typography>
                        <Typography color="text.secondary" fontWeight="bold">Dental Office: {job.location.practiceName}</Typography>
                        <Typography color="var(--primary-main)" mt={1}>
                        You are about to cancel your shift commitment {gracePeriod ? '' : 'just '}{timeBeforeStartString} - this will cause a {gracePeriod ? '' : 'significant '}problem for {job.location.practiceName} and their patients.
                        </Typography>
                    </div>
                    <Alert severity={gracePeriod ? "success" : "warning"} sx={{ mb: 2, borderRadius: '1rem' }} style={{height: 'fit-content'}}> {/*'success' | 'info' | 'warning' | 'error'*/}
                    {gracePeriod ? <>
                        <AlertTitle>Grace Period</AlertTitle>
                        This cancellation falls within the Grace Period and will not affect your reliability statistics.
                    </> : null}
                    {!less24h && !gracePeriod ? <>
                        <AlertTitle>Warning</AlertTitle>
                        Cancelling this shift will affect your Reliability Score and may impact future bookings opportunities.
                    </> : null}
                    {less24h ? <>
                        <AlertTitle>Warning: Short-Notice Cancellation</AlertTitle>
                        This cancellation is within 24 hours of the shift start, penalties will apply, please reconsider.
                        </> : null}
                    </Alert>
                </HeaderBox>

                <Box mb={2}>
                    <Typography variant="h6" fontWeight="bold" color="text.primary" mb={1} display="flex" alignItems="center">
                        Your Reliability Statistics
                        <HtmlTooltip 
                                open={openTooltip}
                                onClick={onOpenTooltip}
                                title={<>{`Your Reliability Score is based on a blend of completed shifts and cancellations.
                                Avoiding shift cancellations keeps your score strong, highlights your dependability to dental offices,
                                 and leads to more booking opportunities at higher rates. `}
                                 <a href="https://support.tempstars.com/hc/en-us/articles/36272510254867-Reliabilty-Score"
                                  target="_blank" rel="noopener noreferrer" className="external">Learn more...</a>
                                </>}>
                                <IconButton>
                                    <HelpIcon fontSize="small"/>
                                </IconButton>
                            </HtmlTooltip>
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <StatItem
                            label="Reliability Score"
                            newValue={stats.reliabilityScore.new}
                            currentValue={stats.reliabilityScore.current}
                            color={stats.reliabilityScore.new < stats.reliabilityScore.current ? "error" : "success.main"}
                        />
                        {/* <StatItem
                            label="Reliability Status"
                            newValue={stats.reliabilityStatus.new}
                            currentValue={stats.reliabilityStatus.current}
                            color={stats.reliabilityStatus.new > stats.reliabilityStatus.current ? "error" : "success.main"}
                        /> */}
                        {showAllStats && (
                            <>
                                <StatItem
                                    label="Cancellation Score"
                                    newValue={stats.cancellationScore.new}
                                    currentValue={stats.cancellationScore.current === -1 ? 'Not yet rated' : stats.cancellationScore.current}
                                    color={stats.cancellationScore.new > stats.cancellationScore.current ? "error" : "success.main"}
                                />
                                <StatItem
                                    label="Cancellations Last 90 Days"
                                    newValue={stats.cancellationsLast90Days.new}
                                    currentValue={stats.cancellationsLast90Days.current === -1 ? 'Not yet rated' : stats.cancellationsLast90Days.current}
                                    color={stats.cancellationsLast90Days.new > stats.cancellationsLast90Days.current ? "error" : "success.main"}
                                />
                                <StatItem
                                    label="Cancellations for Lifetime"
                                    newValue={stats.cancellationsLifetime.new}
                                    currentValue={stats.cancellationsLifetime.current === -1 ? 'Not yet rated' : stats.cancellationsLifetime.current}
                                    color={stats.cancellationsLifetime.new > stats.cancellationsLifetime.current ? "error" : "success.main"}
                                />
                            </>
                        )}
                        <Button
                            onClick={() => setShowAllStats(!showAllStats)}
                            endIcon={<ExpandMoreIcon sx={{ transform: showAllStats ? 'rotate(180deg)' : 'none' }} />}
                            sx={{ mt: 1 }}
                        >
                            {showAllStats ? "Show less" : "Show more"}
                        </Button>
                    </Paper>
                </Box>

                <Box mb={2}>
                    <Typography variant="h6" fontWeight="bold" color={gracePeriod ? "success" : "warning"} mb={1}>Consequences of Cancellation</Typography>
                    <List disablePadding>
                        {!statsLoading && consequences.map((consequence, index) => (
                            <ListItem key={index} disablePadding>
                                <ListItemText primary={consequence} primaryTypographyProps={{ display: 'flex' }}/>
                            </ListItem>
                        ))}
                    </List>
                </Box>

                <Box mb={2} display="flex" flexDirection="column" gap={1}>
                    <Typography variant="h6" fontWeight="bold" color="text.primary" mb={1}>Cancellation Reason</Typography>
                    <FormMaterialSelector
                        control={control}
                        className={styles.styledSelector}
                        name={"reason"}
                        label="Please choose a reason"
                    >
                        {cancelReasons.map((reason, index) => (
                            <MenuItem value={reason.value} key={reason.value + index}>{reason.text}</MenuItem>
                        ))}
                    </FormMaterialSelector>
                    <FormMaterialInput
                        control={control}
                        name={'details'}
                        label={"Please provide any additional details..."}
                        multiline
                        rows={4}
                    />
                </Box>

                <BottomContainer ref={bottomRef}>
                    {!isConfirming ? (
                        <StyledButton
                            onClick={handleCancelClick}
                            variant="contained"
                            color="error"
                            fullWidth
                            disabled={!watch("reason") || !watch("details")}
                        >
                            Cancel Shift
                        </StyledButton>
                    ) : (
                        <Box>
                            <Typography color="text.secondary" mb={1}>Are you sure you need to cancel this shift?</Typography>
                            <Box display="flex" gap={2}>
                                <StyledButton
                                    onClick={() => setIsConfirming(false)}
                                    variant="outlined"
                                    color="inherit"
                                    fullWidth
                                >
                                    No, Keep Shift
                                </StyledButton>
                                <StyledButton
                                    onClick={handleSubmit(onCancel)}
                                    variant="contained"
                                    color="error"
                                    fullWidth
                                >
                                    Yes, Cancel Shift
                                </StyledButton>
                            </Box>
                        </Box>
                    )}
                </BottomContainer>

            </Container>

            <FixedLoader loading={loading || statsLoading}/>
        </>
    );
}

export default CancelJobPage;